<template>
  <article class="pt-0 position-relative acc_list_main_accommodation_item mb-3"
           itemscope itemtype="https://schema.org/Hotel"
           :class="{'top': isTopOffer, 'map_selected': isActiveSelectedOnMap}">
    <div class="card">
      <div class="card-body p-3">
        <div class="row">
          <div class="col-12 col-md-4 col-lg-12 col-xl-4 image-content mb-3 mb-md-0 mb-xl-0">
            <figure class="position-relative">
              <ClientOnly>
                <LazyImageSlider :item="accommodation" :key="`image_slider_square_${accommodation.id}`"
                                 @hrefAction="trackRedirect"
                                 :hrefLink="accommodation.getLinkWithSlash(activeLocale.code, activeCountry.code)"/>
                <template #fallback>

                  <picture>
                    <PartsSrcSetImage v-if="accommodation.hasImage()" :image="accommodation.getTopImage()"
                                      :t_type="'thumbw'"
                                      :type="'webp'"/>
                    <PartsSrcSetImage v-if="accommodation.hasImage()" :image="accommodation.getTopImage()"
                                      :t_type="'thumbw'"
                                      :type="'jpeg'"/>
                    <img v-if="accommodation.hasImage()" :src="accommodation.getTopImage().getTyrionSrc('thumbw')"
                         :alt="accommodation.getTopImage().title " class="big-photo img-cover" loading="lazy"
                         :width="accommodation.getTopImage().seoWidth('thumb')"
                         itemprop="photo"
                         :height="accommodation.getTopImage().seoHeight('thumb')">
                    <img v-else src="~/assets/img/blank.jpg" :alt="accommodation.title" class="img-cover" loading="lazy"
                         width="600" height="337">
                  </picture>
                  <figcaption
                      :class="{'is_last_minute': accommodation.has_last_minute, 'is_top': accommodation.is_top}">
                    <div class="lastminute p-1 px-3" v-if="accommodation.has_last_minute">lastminute</div>
                    <div class="top p-1 px-3" v-if="accommodation.is_top">top</div>
                  </figcaption>

                </template>
              </ClientOnly>
              <figcaption>
                <LazyFavouriteHeartButton :item="accommodation" :iconClass="'icon-color-first mt-2'" :likeText="''"
                                          :dislike-text="''"/>
              </figcaption>
            </figure>
          </div>
          <div class="col-12 col-md-8 col-lg-12 col-xl-8">
            <NuxtLink class="text-decoration-none text-dark"
                      :to="accommodation.getLinkWithSlash(activeLocale.code, activeCountry.code)"
                      :target="hrefTarget" @click="trackRedirect">
              <meta itemprop="url"
                    :content="useRuntimeConfig().public.WEB_URL + accommodation.getLinkWithSlash(activeLocale.code, activeCountry.code)">
              <div class="row d-flex h-100">

                <div class="col-12 pb-3">

                  <h2 class="mb-0 h3 mt-3 mt-md-0 mt-lg-3 mt-xl-4" itemprop="name">
                    {{ accommodation.title }}
                  </h2>
                  <small class="text-muted">{{ $t('acc-type-' + accommodation.category) }},
                    max&nbsp;{{
                      accommodation.max_hosts_capacity
                    }}&nbsp;{{ $t($vysklonujSlovo('host', accommodation.max_hosts_capacity, 'ia', 'i')) }}</small>

                </div>
                <div class="col-12">
                  <div class="content d-block d-md-flex d-lg-block d-xl-flex align-content-end mx-0 px-0 w-100">
                    <div class="col-12 col-md-8 col-lg-12 col-xl-8">
                      <div>
                        <TyIcon icon="ty-star" width="1.2rem" class="icon-color-first pb-1 me-2"/>
                        <span>
                    <strong>{{ accommodation.review_rating > 0 ? accommodation.review_rating + '/5' : '' }}</strong>
                    <span class="text-muted ms-4" v-if="accommodation.review_count > 0">
                      {{
                        accommodation.review_count
                      }}&nbsp;{{ $t($vysklonujSlovo('recenzi', accommodation.review_count, 'e', 'i', 'a')) }}
                    </span>
                    <span class="text-muted" v-else>
                      {{ $t('no-reviews') }}
                    </span>
                  </span>
                      </div>
                      <div class="d-flex justify-content-start"
                           itemprop="address" itemscope itemtype="https://schema.org/PostalAddress">
                        <TyIcon icon="ty-map-pointer" width="1.2rem" class="icon-color-first pb-1 d-inline-block"/>
                        <span class="ms-2 text-muted small"
                              v-if="accommodation.location && accommodation.location.city">
                          {{ accommodation.location.city.title }},
                          {{ accommodation.location.street }} {{ accommodation.location.street_number }},
                          {{ accommodation.location.country.title }}

                          <meta itemprop="postalCode" :content="accommodation.location.city.postcode"/>
                          <meta itemprop="addressLocality" :content="accommodation.location.city.title"/>
                          <meta itemprop="addressRegion" :content="accommodation.location.region.title"/>
                          <meta itemprop="addressCountry" :content="accommodation.location.country.title"/>
                      </span>
                      </div>
                    </div>
                    <div class="col-12 col-md-4 col-lg-12 col-xl-4 price text-end mt-lg-3 mt-xl-0 mt-3 mt-md-0">
                      <p class="mb-0 h5 first-color d-block"
                         v-if="accommodation.price_from > 0 && accommodation.price_from !== 9999">
                        <small v-if="tripDuration === 0" class="text-silver me-2">{{ $t('from') }}</small>
                        <strong itemprop="priceRange">
                          {{ formatPrice(accommodation.price_from, activeLocale.code) }}
                        </strong>&nbsp;/&nbsp;{{ $t('night') }}
                      </p>
                      <p class="mb-0"
                         v-if="tripDuration > 0 && accommodation.price_from != 9999 && totalTripPrice !== false && !isNaN(totalTripPrice)">
                        {{ formatPrice(totalTripPrice, activeLocale.code) }} {{ $t('total') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">

import LazyFavouriteHeartButton from "~/components/web/parts/favourite_heart_button";
import "~/assets/less/acc_list_main_accommodation_item.min.css";
import dayjs from "dayjs";
import {computed, PropType, resolveComponent} from "vue";
import Accommodation from "~/lib/models/Accommodation";
import Client from "~/lib/models/Client";
import useStateAccommodationList from "~/composables/state/accommodation_list";
import {useNuxtApp, useRuntimeConfig} from "#app";
import ImageSlider from "./acc_list_main_accommodation_item_image_slider.vue";
import {formatPrice, switchCurrencyMark} from "../../../../../lib/functions";

const props = defineProps({
  accommodation: Object as PropType<Accommodation>,
  client: Object as PropType<Client>,
  activeLocale: Object as PropType<{ code: string }>,
  activeCountry: Object as PropType<{ code: string }>,
  params: Object,
  currency: Object as PropType<{ code: String, name: String, mark: String }>,
  widgetName: String,
  GATrackEvent: Function
})
const LazyImageSlider = resolveComponent("LazyWidgetAccommodationListAccListMainAccommodationItemImageSlider");
const {selectedMapItem} = useStateAccommodationList();
const {$isDesktop} = useNuxtApp();

const isTopOffer = computed(() => {
  return props.accommodation.id === 5
});

const isActiveSelectedOnMap = computed(() => {
  return selectedMapItem.value === props.accommodation.id;
});

const totalTripPrice = computed(() => {
  const totalPrice = props.accommodation.price_from;
  const duration = tripDuration.value;
  return !isNaN(totalPrice) && duration > 0 ? (Math.round(totalPrice * duration)) : false;
});

const tripDuration = computed(() => {
  if (props.params && props.params.dateFrom && props.params.dateTo) {
    return Number(dayjs(props.params.dateTo).diff(dayjs(props.params.dateFrom), 'day'));
  }
  return 0;
});


const hrefTarget = computed(() => {
  return "_self"
});

const trackRedirect = () => {
  if (props.GATrackEvent && props.widgetName) {
    props.GATrackEvent({
      event: 'open_accommodation',
      action: 'redirect',
      label: 'Redirect to accommodation from list item',
      value: props.accommodation.link,
      widget: props.widgetName,
      items: [
        {
          item_id: props.accommodation.id,
          item_name: props.accommodation.title,
          item_category: props.accommodation.category,
          item_variant: props.accommodation.location.tourist_region ? props.accommodation.location.tourist_region.code : null
        }
      ]
    }, 0);
  }
}
</script>
